import { NotificationType } from '@/types';
import { HcpVerificationStatus } from '@heidi/schema/ml-scribe';

/**
 * Must stay byte-identical to the titles in ml-scribe
 * `domains/notifications/service/notification_service.py`. Refuse reuses the
 * daily-nag notification name and is only distinguishable by this copy.
 */
export const HCP_VERIFICATION_NAG_INMAIL_TITLE = 'Verify your details';
export const HCP_VERIFICATION_REJECTED_INMAIL_TITLE = "We couldn't verify your details";

export interface HcpStatusResolutionInmail {
  id?: string | null;
  notification_name?: string | null;
  title?: string | null;
  created_at?: string | null;
}

export type HcpInmailStatusKind = 'verified' | 'rejected';

export interface HcpInmailStatusSignal {
  id: string;
  kind: HcpInmailStatusKind;
}

const toTime = (value: string | null | undefined): number => {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Daily nags are not a status change. Grant is its own name. Refuse reuses
 * the nag name and is identified by the rejected title; any other needed
 * title is treated as a refuse so a copy change cannot hide a rejection.
 */
export const classifyHcpInmail = (
  inmail: HcpStatusResolutionInmail
): HcpInmailStatusKind | 'nag' | null => {
  if (inmail.notification_name === NotificationType.HCP_VERIFICATION_VERIFIED) {
    return 'verified';
  }
  if (inmail.notification_name !== NotificationType.HCP_VERIFICATION_NEEDED) {
    return null;
  }
  if (inmail.title === HCP_VERIFICATION_NAG_INMAIL_TITLE) return 'nag';
  return 'rejected';
};

/** The newest grant or refuse. A leftover daily nag never wins. */
export const latestHcpStatusSignal = (
  inmails: ReadonlyArray<HcpStatusResolutionInmail> | undefined
): HcpInmailStatusSignal | null => {
  let latest: { signal: HcpInmailStatusSignal; at: number } | null = null;
  for (const inmail of inmails ?? []) {
    if (!inmail.id) continue;
    const kind = classifyHcpInmail(inmail);
    if (kind !== 'verified' && kind !== 'rejected') continue;
    const at = toTime(inmail.created_at);
    if (latest === null || at >= latest.at) {
      latest = { signal: { id: inmail.id, kind }, at };
    }
  }
  return latest?.signal ?? null;
};

/** Ids of every grant and refuse in the list. Daily nags are not signals. */
export const hcpStatusSignalIds = (
  inmails: ReadonlyArray<HcpStatusResolutionInmail> | undefined
): ReadonlySet<string> => {
  const ids = new Set<string>();
  for (const inmail of inmails ?? []) {
    if (!inmail.id) continue;
    const kind = classifyHcpInmail(inmail);
    if (kind === 'verified' || kind === 'rejected') ids.add(inmail.id);
  }
  return ids;
};

const statusMatchesSignal = (
  status: HcpVerificationStatus | null,
  kind: HcpInmailStatusKind
): boolean =>
  status ===
  (kind === 'verified' ? HcpVerificationStatus.VERIFIED : HcpVerificationStatus.REJECTED);

/**
 * The newest grant/refuse the status cache has not yet answered for.
 *
 * Disagreement alone cannot say which side is stale: a user who resubmits a
 * rejected registry replaces that record with `NEEDS_REVIEW` and no inmail
 * is written, so the bell keeps a refuse that is older than a pending cache
 * that is right. The only other way to see "newest is a refuse, cache is
 * pending" is a reviewer rejecting — where the bell is right. Ordering
 * separates them: an inmail that was already in the bell when the status
 * last settled was committed before that read, so the cache has seen it.
 *
 * `status` is the cached rank: `null` for no record, `undefined` while the
 * cache is unsettled. Returns `null` while unsettled, when only a daily nag
 * exists, when the newest row was in `bellAtSettle`, or when the banner
 * already matches.
 */
export const hcpStatusSignalToHeal = (
  inmails: ReadonlyArray<HcpStatusResolutionInmail> | undefined,
  status: HcpVerificationStatus | null | undefined,
  bellAtSettle: ReadonlySet<string>
): HcpInmailStatusSignal | null => {
  if (status === undefined) return null;
  const latest = latestHcpStatusSignal(inmails);
  if (latest === null || bellAtSettle.has(latest.id)) return null;
  return statusMatchesSignal(status, latest.kind) ? null : latest;
};
